// The two questions a rollup answers — which result wins, how much each member counts — and
// their mapping onto the stored `aggregationMethod` + `weighting` pair. Pure; no React.

import { resolveRollupWeighting } from '@center-platform/spheres/rollup';
import type { AggregationMethod, RollupWeighting } from 'types/spherePoints';

export type RollupWins = 'worst' | 'balanced' | 'majority';

export interface CombineAxes {
  wins: RollupWins;
  weighting: RollupWeighting;
}

export interface StoredCombine {
  aggregationMethod: AggregationMethod;
  weighting: RollupWeighting;
}

/** Raw stored values: a known method / weighting, or a legacy or unknown string. */
interface StoredCombineInput {
  aggregationMethod?: string | null;
  weighting?: string | null;
  influenceFloor?: number | null;
}

/** `weighted` is the stored name for *Balanced + By influence*; *Balanced + Equally* is stored as `average`. */
export function toStored({ wins, weighting }: CombineAxes): StoredCombine {
  if (wins === 'balanced') {
    return weighting === 'influence'
      ? { aggregationMethod: 'weighted', weighting: 'influence' }
      : { aggregationMethod: 'average', weighting: 'equal' };
  }
  return { aggregationMethod: wins, weighting };
}

/** Inverse of `toStored`. A legacy factor without a `weighting` resolves through the same rule the server uses. */
export function fromStored(input: StoredCombineInput): CombineAxes {
  const method = input.aggregationMethod || 'worst';
  const weighting = resolveRollupWeighting({ ...input, aggregationMethod: method });
  switch (method) {
    case 'weighted':
      return { wins: 'balanced', weighting: 'influence' };
    case 'average':
      return { wins: 'balanced', weighting: 'equal' };
    case 'majority':
      return { wins: 'majority', weighting };
    // `percentage` is no longer offered; it opens as Balanced, which scores identically (D3, D102).
    case 'percentage':
      return { wins: 'balanced', weighting };
    default:
      return { wins: 'worst', weighting };
  }
}
